import Image from "next/image";
import React, { ReactElement } from "react";
import Head from "next/head";
import { MainLayout } from "@/containers/layout/NowLayout";
import ReferralCode from "@/components/refer/ReferralCode";
import ReferralFAQs from "@/components/refer/ReferralFAQs";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import HowItWorks from "@/components/refer/HowItWorks";
import { sendReferralLinkEmail } from "@/services/api";
import { toast } from "react-toastify";
import { useAuth } from "@/shared/hooks/useAuth";

const Refer = (): JSX.Element => {
  const router = useRouter();
  const [email, setEmail] = useState<string | null>(null);
  const { user, isAuthenticated } = useAuth({ redirectOnUnauth: false });

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  useEffect(() => {
    if (router.isReady) {
      const { e } = router.query;
      if (e && typeof e === "string") {
        setEmail(e);
      }
    }
  }, [router.isReady, router.query]);

  const sendReferralEmail = async (email: string) => {
    try {
      const response = await sendReferralLinkEmail({ email });
      if (response.success) {
        toast.success("Referral email sent successfully!");
      } else {
        toast.error("Failed to send referral email. Please try again later.");
      }
    } catch (error) {
      toast.error("Failed to send referral email. Please try again later.");
    }
  };

  const handleEmailSubmit = (submittedEmail: string) => {
    // Update the URL with the email parameter
    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, e: submittedEmail },
      },
      undefined,
      { shallow: true },
    );
    setEmail(submittedEmail);
    sendReferralEmail(submittedEmail);
  };
  return (
    <>
      <Head>
        <title>Refer A Friend to Keyzy</title>
        <link rel="canonical" href="https://www.keyzy.com/refer" />
        <meta
          name="description"
          key="description"
          content="Refer a friend to Keyzy and be rewarded. Help them take the first step towards homeownership with our rent-to-buy solution. Fixed rent, no upfront deposit, and a flexible path to buying their dream home in London. Sign up today and we'll take care of the rest!"
        />
        <meta
          name="twitter:card"
          content="Refer a friend to Keyzy and be rewarded. Help them take the first step towards homeownership with our rent-to-buy solution. Fixed rent, no upfront deposit, and a flexible path to buying their dream home in London. Sign up today and we'll take care of the rest!"
          key="twcard"
        />
        <meta name="twitter:creator" content="keyzyhomes" key="twhandle" />
        <meta property="og:url" content="/" key="ogurl" />
        <meta property="og:image" content="/refer/meta-og-refer-a-friend.png" key="ogimage" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Keyzy" key="ogsitename" />
        <meta property="og:title" content="Refer A Friend to Keyzy" key="ogtitle" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_GB" />
        <meta
          property="og:description"
          content="Refer a friend to Keyzy and be rewarded. Help them take the first step towards homeownership with our rent-to-buy solution. Fixed rent, no upfront deposit, and a flexible path to buying their dream home in London. Sign up today and we'll take care of the rest!"
          key="ogdesc"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://keyzy.com/" },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Refer a Friend",
                  item: "https://keyzy.com/refer",
                },
              ],
            }),
          }}
        />
      </Head>

      <div className="flex flex-col lg:flex-row px-4 xl:px-24 xl:pt-24 md:pb-20">
        <div className="w-full lg:w-1/2 h-auto flex-col justify-start items-start gap-2 inline-flex mr-5 mb-5 md:mb-0">
          <div className="self-stretch text-slate-800 text-[2rem] md:text-5xl font-bold">
            <span className="text-landing-orange">Refer</span> a friend
          </div>
          <div className="self-stretch text-stone-900 text-2xl font-normal leading-9">
            Know someone looking to buy or rent in London? Refer them to Keyzy and{" "}
            <span className="font-bold">earn £250</span> when they sign!
          </div>
          <div className="hidden md:flex mt-[5.5rem]">
            <Image src="/refer/refer-a-friend.svg" width={516} height={487} alt="Refer a friend" />
          </div>
        </div>
        <div className="w-full lg:w-1/2 h-auto flex-col justify-start items-start gap-2 inline-flex">
          <ReferralCode
            email={email || undefined}
            onSubmit={handleEmailSubmit}
            hideChangeEmail={isAuthenticated}
          />
          <HowItWorks email={email || undefined} />
          <ReferralFAQs />
          <div className="md:hidden mt-2 mb-5">
            <Image src="/refer/refer-a-friend.svg" width={516} height={487} alt="Refer a friend" />
          </div>
        </div>
      </div>
    </>
  );
};

Refer.getLayout = function getLayout(page: ReactElement) {
  return (
    <>
      <MainLayout theme="lightBlue">{page}</MainLayout>
    </>
  );
};

export default Refer;
